/**
 * Excel/스프레드시트 표 서식 정규화 (P6-②)
 * kingdoo-editor의 excel-paste.ts 색 양자화 로직을 vanilla로 이식.
 *
 * 스프레드시트(엑셀 온라인/구글 시트/워드)는 셀 서식을 CSS 인라인 스타일이나
 * 표현 속성(bgcolor/align/valign)으로 내보낸다. htmlToInlineContent는 임의 CSS 색을
 * 읽지 않으므로(span의 data-* 만 인식), 셀 서식을 우리 모델이 인식하는 형태로 변환한다:
 *  - CSS color/background → 가장 가까운 10색 팔레트(value) → 셀 data-* (셀 prop)
 *  - text-align/align → data-text-alignment, vertical-align/valign → data-vertical-alignment
 *  - font-weight/style/underline → <strong>/<em>/<u> 래핑, font-size → data-font-size span
 *
 * 한계: 데스크톱 엑셀의 클래스 기반(<style>.xl65{background:#FF0})은 getComputedStyle이
 *   필요해 여기 인라인 경로로는 잡지 못한다(엑셀 온라인/시트/워드는 인라인 → 대부분 커버).
 */

const NAMED_COLORS = {
  black: '#000000',
  white: '#ffffff',
  red: '#ff0000',
  green: '#008000',
  blue: '#0000ff',
  yellow: '#ffff00',
  orange: '#ffa500',
  purple: '#800080',
  gray: '#808080',
  grey: '#808080',
};

/** CSS 색 문자열(#hex, rgb(), 일부 named) → [r,g,b]. 실패 시 null */
export function parseCssColorToRgb(input) {
  if (!input) return null;
  const s = String(input).trim().toLowerCase();
  if (!s || s === 'transparent' || s === 'none' || s === 'inherit') return null;
  let m = s.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/);
  if (m) {
    let h = m[1];
    if (h.length === 3)
      h = h
        .split('')
        .map((c) => c + c)
        .join('');
    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16),
    ];
  }
  m = s.match(/^rgba?\(([^)]+)\)$/);
  if (m) {
    const parts = m[1].split(/[,/]/).map((x) => parseFloat(x.trim()));
    if (parts.length >= 3 && parts.slice(0, 3).every((n) => !isNaN(n))) {
      // 완전 투명(alpha=0)은 무배경/무색. getComputedStyle이 서식 없는 셀에
      // 대해 rgba(0,0,0,0)을 돌려주는데, 이를 검정(→gray)으로 오인하지 않도록 제외.
      if (parts.length >= 4 && parts[3] === 0) return null;
      return [parts[0], parts[1], parts[2]];
    }
    return null;
  }
  if (NAMED_COLORS[s]) return parseCssColorToRgb(NAMED_COLORS[s]);
  return null;
}

function rgbToHsl([r, g, b]) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0,
    s = 0;
  const d = max - min;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
    else if (max === g) h = ((b - r) / d + 2) * 60;
    else h = ((r - g) / d + 4) * 60;
  }
  return [h, s, l];
}

const HUE_REFERENCE = [
  { value: 'red', hue: 0 },
  { value: 'brown', hue: 17 },
  { value: 'orange', hue: 30 },
  { value: 'yellow', hue: 46 },
  { value: 'green', hue: 150 },
  { value: 'blue', hue: 197 },
  { value: 'purple', hue: 262 },
  { value: 'pink', hue: 324 },
];

function hueDist(a, b) {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

function paletteValueFromRgb(rgb) {
  const [h, s, l] = rgbToHsl(rgb);
  if (s < 0.15) {
    if (l < 0.35) return 'default'; // 검정류
    if (l > 0.85) return 'default'; // 흰색류
    return 'gray';
  }
  let best = 'gray',
    bestDist = Infinity;
  for (const ref of HUE_REFERENCE) {
    const d = hueDist(h, ref.hue);
    if (d < bestDist) {
      bestDist = d;
      best = ref.value;
    }
  }
  return best;
}

/** 임의 색 → 가장 가까운 텍스트 팔레트 value (검정류는 default) */
export function nearestTextColorValue(rgb) {
  return paletteValueFromRgb(rgb);
}

/** 임의 색 → 가장 가까운 배경 팔레트 value (흰색 근사만 default=무배경) */
export function nearestBackgroundColorValue(rgb) {
  const [, s, l] = rgbToHsl(rgb);
  // 배경은 텍스트와 달리 검정/진회색을 default(무배경)로 버리면 어두운 채움이 사라진다.
  // 흰색 근사만 default(무배경)로 두고, 나머지 저채도(검정~회색)는 gray로 강등한다.
  if (s < 0.15 && l > 0.85) return 'default'; // 흰색/근백색 = 무배경
  if (s < 0.15) return 'gray'; // 검정·진회색·중간회색 = 회색 배경
  return paletteValueFromRgb(rgb);
}

function parseStyle(style) {
  const out = {};
  String(style || '')
    .split(';')
    .forEach((decl) => {
      const idx = decl.indexOf(':');
      if (idx === -1) return;
      const k = decl.slice(0, idx).trim().toLowerCase();
      const v = decl.slice(idx + 1).trim();
      if (k) out[k] = v;
    });
  return out;
}

function colorFromBackgroundShorthand(value) {
  if (!value) return null;
  const direct = parseCssColorToRgb(value);
  if (direct) return direct;
  for (const token of String(value).split(/\s+/)) {
    const rgb = parseCssColorToRgb(token);
    if (rgb) return rgb;
  }
  return null;
}

function normalizeAlign(ta) {
  const v = String(ta || '')
    .trim()
    .toLowerCase();
  if (v === 'right' || v === 'end') return 'right';
  if (v === 'center') return 'center';
  return ''; // left/justify/start → 기본
}

function mapVerticalAlign(v) {
  const s = String(v || '')
    .trim()
    .toLowerCase();
  if (s === 'middle' || s === 'center') return 'middle';
  if (s === 'bottom') return 'bottom';
  return null;
}

function fontSizeToPx(raw) {
  if (!raw) return null;
  const m = String(raw)
    .trim()
    .match(/^([\d.]+)\s*(px|pt)?$/i);
  if (!m) return null;
  const v = parseFloat(m[1]);
  if (!Number.isFinite(v) || v <= 0) return null;
  return (m[2] || 'px').toLowerCase() === 'pt' ? v * (96 / 72) : v;
}

/** 셀(td/th)의 인라인 style + 표현 속성으로 서식 읽기 */
function readInlineFormat(el) {
  const sm = parseStyle(el.getAttribute('style'));
  const bgRaw = sm['background-color'] || sm['background'] || '';
  const bgRgb =
    colorFromBackgroundShorthand(bgRaw) ||
    parseCssColorToRgb(el.getAttribute('bgcolor'));
  const fw = (sm['font-weight'] || '').toLowerCase();
  const decoration = sm['text-decoration'] || sm['text-decoration-line'] || '';
  return {
    bgRgb,
    colorRgb: parseCssColorToRgb(sm['color']),
    align: normalizeAlign(sm['text-align'] || el.getAttribute('align')),
    bold: fw === 'bold' || fw === 'bolder' || parseInt(fw, 10) >= 600,
    italic: (sm['font-style'] || '').toLowerCase().includes('italic'),
    underline: decoration.toLowerCase().includes('underline'),
    fontSizePx: fontSizeToPx(sm['font-size']),
    verticalAlign: mapVerticalAlign(
      sm['vertical-align'] || el.getAttribute('valign'),
    ),
  };
}

/** 셀 서식을 우리 모델이 인식하는 data-* 속성/마크 래핑으로 적용 */
function applyCellFormatting(el, fmt) {
  if (fmt.bgRgb) {
    const v = nearestBackgroundColorValue(fmt.bgRgb);
    if (v !== 'default') el.setAttribute('data-background-color', v);
  }
  if (fmt.colorRgb) {
    const v = nearestTextColorValue(fmt.colorRgb);
    if (v !== 'default') el.setAttribute('data-text-color', v);
  }
  if (fmt.align === 'right' || fmt.align === 'center')
    el.setAttribute('data-text-alignment', fmt.align);
  if (fmt.verticalAlign)
    el.setAttribute('data-vertical-alignment', fmt.verticalAlign);
  if ((fmt.bold || fmt.italic || fmt.underline) && el.innerHTML.trim()) {
    let inner = el.innerHTML;
    if (fmt.underline) inner = `<u>${inner}</u>`;
    if (fmt.italic) inner = `<em>${inner}</em>`;
    if (fmt.bold) inner = `<strong>${inner}</strong>`;
    el.innerHTML = inner;
  }
  if (
    fmt.fontSizePx &&
    Math.abs(fmt.fontSizePx - 14) > 1 &&
    el.innerHTML.trim()
  ) {
    const v = `${Math.round(fmt.fontSizePx)}px`;
    el.innerHTML = `<span data-font-size="${v}" style="font-size:${v}">${el.innerHTML}</span>`;
  }
}

/** 표 요소의 셀 서식을 제자리 정규화(인라인 경로). parseHtmlToBlocks가 호출. */
export function normalizeExcelTableEl(tableEl) {
  if (!tableEl) return;
  tableEl.querySelectorAll('td, th').forEach((cell) => {
    applyCellFormatting(cell, readInlineFormat(cell));
  });
}

/** getComputedStyle로 셀 서식 읽기(클래스/<style>/inline 모두 반영) */
function readComputedFormat(el) {
  const cs = getComputedStyle(el);
  const fw = cs.fontWeight;
  const fwNum = parseInt(fw, 10);
  const decoration = cs.textDecorationLine || cs.textDecoration || '';
  return {
    bgRgb: parseCssColorToRgb(cs.backgroundColor),
    colorRgb: parseCssColorToRgb(cs.color),
    align: normalizeAlign(cs.textAlign),
    bold: fw === 'bold' || fw === 'bolder' || (!isNaN(fwNum) && fwNum >= 600),
    italic: (cs.fontStyle || '').toLowerCase().includes('italic'),
    underline: decoration.toLowerCase().includes('underline'),
    fontSizePx: fontSizeToPx(cs.fontSize),
    // computed vertical-align은 td 기본 middle 노이즈 → 명시 inline/valign만
    verticalAlign: mapVerticalAlign(
      (el.style && el.style.verticalAlign) || el.getAttribute('valign'),
    ),
  };
}

/**
 * 클립보드 HTML의 표 셀 서식을 정규화한 HTML 반환(데스크톱 엑셀 클래스 기반 색 대응).
 * 엑셀(데스크톱)은 셀 서식을 <style>.xlNN{background:#..} 클래스로 내보낸다. 격리된 Shadow DOM에
 * 잠깐 붙여 getComputedStyle로 최종 서식을 읽어 data-* 속성·마크로 굽고, 원본 셀에 반영해 HTML로 직렬화.
 * 미지원/비브라우저 환경은 원본 그대로(인라인 경로가 처리).
 */
export function normalizeExcelClipboardHtml(html) {
  if (!html || typeof DOMParser === 'undefined') return html;
  if (
    typeof document === 'undefined' ||
    !document.body ||
    typeof HTMLElement === 'undefined' ||
    typeof HTMLElement.prototype.attachShadow !== 'function' ||
    typeof getComputedStyle === 'undefined'
  )
    return html;
  let doc;
  try {
    doc = new DOMParser().parseFromString(html, 'text/html');
  } catch (_) {
    return html;
  }
  doc.querySelectorAll('script').forEach((s) => s.remove());
  const tables = [...doc.querySelectorAll('table')];
  if (!tables.length) return html;
  const styles = [...doc.querySelectorAll('style')]
    .map((s) => s.outerHTML)
    .join('');
  if (!styles) return html; // 클래스 기반 스타일 없으면 인라인 경로로 충분
  let host = null;
  try {
    host = document.createElement('div');
    host.setAttribute('aria-hidden', 'true');
    host.style.cssText =
      'position:absolute;left:-99999px;top:0;width:0;height:0;overflow:hidden;opacity:0;pointer-events:none;font-size:14px';
    const shadow = host.attachShadow({ mode: 'open' });
    shadow.innerHTML = styles + tables.map((t) => t.outerHTML).join('');
    document.body.appendChild(host);
    const shadowTables = [...shadow.querySelectorAll('table')];
    tables.forEach((t, ti) => {
      const st = shadowTables[ti];
      if (!st) return;
      const origCells = [...t.querySelectorAll('td, th')];
      const shadowCells = [...st.querySelectorAll('td, th')];
      origCells.forEach((cell, ci) => {
        const sc = shadowCells[ci];
        if (sc) applyCellFormatting(cell, readComputedFormat(sc));
      });
    });
    return doc.body.innerHTML;
  } catch (_) {
    return html;
  } finally {
    if (host && host.parentNode) host.parentNode.removeChild(host);
  }
}
